import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Button,
} from "@mui/material"
import DataTable from "@/components/Tables/DataTable"
import { columns } from "./columns"
import Chart from "@/components/charts/Chart"
import { useEffect, useState } from "react"
import { apiFetch } from "@/hooks/apiFetch"
import "rsuite/dist/rsuite.min.css"
import { exportReport } from "@/hooks/useReportExport"
import { useAppSelector } from "@/lib/hooks"
import { BrandSelect } from "../../Brand/BrandSelect"
import { useReportBranch } from "@/components/Reports/ReportBranchFilter"

const LowQuantityProductReport = () => {
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [threshold, setThreshold] = useState(10)
  const [limit, setLimit] = useState(20)
  const [sort, setSort] = useState("asc")
  const [categories, setCategories] = useState([])
  const [selectedCategory, setSelectedCategory] = useState("all")
  const { branchId } = useReportBranch()

  // export Daily Report
  const handleExport = async () => {
    return exportReport({
      startDate: "",
      endDate: "",
      reportType: "low-quantity-product",
      threshold: threshold.toString(),
      limit: limit.toString(),
      sort: sort,
      brand_id: selectedBrandId,
      branchId,
    })
  }

  const selectedBrandId = useAppSelector((state) => state.brand.selectedBrandId)
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        setError(null)

        // Fetch categories first if you want category filtering
        // const cats = await apiFetch("product-categories");
        // setCategories(cats);

        const params = new URLSearchParams({
          threshold: threshold.toString(),
          limit: limit.toString(),
          sort: sort,
          brand_id: selectedBrandId,
        })

        if (selectedCategory !== "all") {
          params.append("category_id", selectedCategory)
        }

        if (branchId) {
          params.set("branch_id", branchId.toString())
        }

        const data = await apiFetch(
          `report/low-quantity-products?${params.toString()}`
        )
        setProducts(data || [])
      } catch (err) {
        console.error(err)
        setError(
          "Failed to fetch low quantity product data. Please try again later."
        )
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [threshold, limit, sort, selectedCategory, selectedBrandId, branchId])

  // Prepare chart data
  const chartData =
    products.length > 0
      ? {
          labels: products.map((item) => item.product_name),
          quantityData: products.map((item) => parseInt(item.current_quantity)),
          valueAtRisk: products.map((item) =>
            parseFloat(item.value_at_risk || 0)
          ),
        }
      : null

  return (
    <Box sx={{ p: 3 }}>
      <div className="flex justify-between">
        <Typography variant="h6" gutterBottom>
          <div>Low Stock Products Report</div>
        </Typography>
        <div className="w-[300px]">
          <BrandSelect />
        </div>
      </div>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Stock Threshold</InputLabel>
                <Select
                  value={threshold}
                  onChange={(e) => setThreshold(e.target.value)}
                  label="Stock Threshold"
                >
                  <MenuItem value={1}>Below 1</MenuItem>
                  <MenuItem value={3}>Below 3</MenuItem>
                  <MenuItem value={5}>Below 5</MenuItem>
                  <MenuItem value={10}>Below 10</MenuItem>
                  <MenuItem value={15}>Below 15</MenuItem>
                  <MenuItem value={20}>Below 20</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Max Results</InputLabel>
                <Select
                  value={limit}
                  onChange={(e) => setLimit(e.target.value)}
                  label="Max Results"
                >
                  <MenuItem value={"all"}>All</MenuItem>
                  <MenuItem value={10}>Top 10</MenuItem>
                  <MenuItem value={20}>Top 20</MenuItem>
                  <MenuItem value={50}>Top 50</MenuItem>
                  <MenuItem value={100}>Top 100</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sort}
                  onChange={(e) => setSort(e.target.value)}
                  label="Sort By"
                >
                  <MenuItem value="asc">Lowest First</MenuItem>
                  <MenuItem value="desc">Highest First</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {/* <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  label="Category"
                >
                  <MenuItem value="all">All Categories</MenuItem>
                  {categories.map((cat) => (
                    <MenuItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid> */}
            <Grid item xs={12} md={1}>
              <Button
                variant="outlined"
                onClick={() => handleExport()}
                sx={{
                  textTransform: "capitalize",
                  borderRadius: "7px",
                  fontWeight: "500",
                  fontSize: "13px",
                  padding: "6px 3px",
                  marginTop: "10px",
                }}
                color="primary"
              >
                Export
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {loading && <Typography>Loading...</Typography>}
      {error && <Typography color="error">{error}</Typography>}

      {/* Summary Cards */}
      {!loading && !error && products.length > 0 && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <Paper elevation={3} sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="subtitle1">
                Products Below Threshold
              </Typography>
              <Typography variant="h5" color="primary">
                {products.length}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper elevation={3} sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="subtitle1">Total Value at Risk</Typography>
              <Typography variant="h5" color="secondary">
                Rs.{" "}
                {products
                  .reduce((sum, item) => sum + (item.value_at_risk || 0), 0)
                  .toLocaleString("en-IN", { maximumFractionDigits: 2 })}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper elevation={3} sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="subtitle1">Most Critical Item</Typography>
              <Typography>
                {products[0]?.product_name} (Only{" "}
                {products[0]?.current_quantity} left)
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Charts */}
      {chartData && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Current Stock Levels
                </Typography>
                <Chart
                  type="bar"
                  data={chartData.quantityData}
                  labels={chartData.labels}
                  title="Current Stock"
                  backgroundColor="rgba(255, 99, 132, 0.5)"
                  borderColor="#ff6384"
                  tooltipLabel="Units"
                />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Value at Risk
                </Typography>
                <Chart
                  type="bar"
                  data={chartData.valueAtRisk}
                  labels={chartData.labels}
                  title="Value at Risk"
                  backgroundColor="rgba(54, 162, 235, 0.5)"
                  borderColor="#36a2eb"
                  tooltipLabel="Rs."
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Data Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Low Stock Products
          </Typography>
          <DataTable
            columns={columns}
            data={products}
            isLoading={loading}
            sx={{ mt: 2 }}
          />
        </CardContent>
      </Card>
    </Box>
  )
}

export default LowQuantityProductReport
